import { useState, useEffect, useCallback } from 'react';
import { studentAPI } from '../services/api';
import { Student } from '../types';

interface UseStudentProfilePhotoResult {
  displayPhoto: string;
  loading: boolean;
  error: string | null;
  /** Call after a successful upload/removal to pull the new photo immediately. */
  refetch: () => void;
}

// Every screen showing a child's photo (the header, the switch-child sheet, the profile) holds
// its own copy. An upload refetched only the profile's copy, so the header kept the initials
// until the app restarted. refetch() now tells every copy of that child to load again.
const photoListeners = new Set<(studentId: string) => void>();

export const notifyStudentPhotoChanged = (studentId: string) => {
  photoListeners.forEach((listener) => listener(studentId));
};

/** Calls onChange whenever this child's photo changes; returns the unsubscribe. */
export const subscribeStudentPhotoChanged = (studentId: string, onChange: () => void) => {
  const listener = (changed: string) => {
    if (changed.toLowerCase() === studentId.toLowerCase()) onChange();
  };
  photoListeners.add(listener);
  return () => {
    photoListeners.delete(listener);
  };
};

const blobToDataUri = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo blob'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });

/**
 * Reads a student's photo from the dedicated /student/{id}/photo endpoint - the
 * same source the web parent portal uses (StudentPhotoService / StudentPhotoController).
 *
 * This used to look for a StudentDocument named "Passport Size Photo", or fall back to a
 * `profileImage` field on whatever student object the caller happened to have. Both are the
 * disconnected, superseded paths StudentPhotoService's own header comment calls out: a photo
 * uploaded through one path was invisible to a screen reading through the other, on this app
 * and against the web portal alike. Every screen now reads the one place a photo can actually
 * be written to (StudentProfile.ProfilePicUrl), so an upload from any client shows up everywhere.
 *
 * The endpoint is [Authorize]d, so the bytes are fetched through the API client (bearer token
 * attached) and turned into a data URI - a plain <Image source={{uri}}> pointed at an
 * authorised URL gets a 401 and silently falls back to initials, which was the visible symptom.
 */
export const useStudentProfilePhoto = (
  studentId: string,
  _student?: Student
): UseStudentProfilePhotoResult => {
  const [displayPhoto, setDisplayPhoto] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refetch = useCallback(() => notifyStudentPhotoChanged(studentId), [studentId]);

  useEffect(() => {
    if (!studentId) return;
    return subscribeStudentPhotoChanged(studentId, () => setRefreshToken((v) => v + 1));
  }, [studentId]);

  useEffect(() => {
    if (!studentId) {
      setDisplayPhoto('');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProfilePhoto = async () => {
      try {
        setLoading(true);
        setError(null);

        const describeResponse = await studentAPI.describeStudentPhoto(studentId);
        const photoVersion =
          describeResponse.data?.photoVersion || describeResponse.data?.PhotoVersion;

        // No photograph on file - components fall back to initials, not an error.
        if (!photoVersion) {
          if (!cancelled) setDisplayPhoto('');
          return;
        }

        const blobResponse = await studentAPI.getStudentPhotoBlob(studentId, photoVersion, false);
        const dataUri = await blobToDataUri(blobResponse.data);
        if (!cancelled) setDisplayPhoto(dataUri);
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to fetch profile photo';
          setError(errorMsg);
          setDisplayPhoto('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfilePhoto();
    return () => {
      cancelled = true;
    };
  }, [studentId, refreshToken]);

  return {
    displayPhoto,
    loading,
    error,
    refetch,
  };
};
